import { GraphQLGeofenceRecommendationsRequester } from '@cobliteam/api-lib';
import type { FleetGeofenceRecommendation } from '@cobliteam/api-lib/src/__generated__/graphql';
import { GeofenceType } from '@cobliteam/common-types/src/geofenceV3';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import useUser from '../useEntity/useUser';
import useFeatureFlags from '../useFeatureFlags';
import useRegisterFleetRecommendations from '../useRegisterFleetRecommendations/useRegisterFleetRecommendations';

export type GeofenceRecommendationType = GeofenceType;

const VALID_TYPES = new Set<string>(Object.values(GeofenceType));

// Anything outside GeofenceType (e.g. legacy GAS_STATION) lands on DEFAULT so
// the prefilled form always has a valid selection.
const normalizeType = (raw: string): GeofenceType => (VALID_TYPES.has(raw) ? (raw as GeofenceType) : GeofenceType.DEFAULT);

export interface GeofenceRecommendation {
  id: string;
  fleetId: string;
  name: string;
  reason: string;
  address: string | null;
  color: string;
  type: GeofenceRecommendationType;
  area: number;
  format: string;
  radius: number;
  geometryPoint: {
    type: 'Point';
    coordinates: [number, number];
  };
}

const STALE_TIME_MS = 10 * 60 * 1000;

const geofenceRecommendationsRequester = new GraphQLGeofenceRecommendationsRequester();

const useGeofenceRecommendations = (options?: { enabled?: boolean }) => {
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;
  const { poisRecommendations } = useFeatureFlags();
  const enabled = !!fleetId && poisRecommendations && options?.enabled !== false;

  useRegisterFleetRecommendations(enabled);

  const query = useQuery({
    // Chave estável, espelhando useGroupRecommendations: as mutations invalidam por esta chave.
    queryKey: ['GEOFENCE_RECOMMENDATIONS', fleetId],
    queryFn: () => geofenceRecommendationsRequester.getFleetGeofenceRecommendations(fleetId!),
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data = useMemo(() => {
    if (!query.data) return query.data;
    return query.data.map((rec: FleetGeofenceRecommendation) => ({
      ...rec,
      type: normalizeType(rec.type),
      geometryPoint: { type: 'Point' as const, coordinates: [rec.longitude, rec.latitude] as [number, number] },
    }));
  }, [query.data]);

  return { ...query, data };
};

export default useGeofenceRecommendations;
